import React, { useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAppConfig } from "@/hooks/useAppConfig";
import StreamText from "./effects/StreamText";
import { Star, ArrowUpRight } from "lucide-react";
import { SOCIAL_PLATFORMS, socialHandle } from "@/lib/socials";

interface MyLinksSectionProps {
  t: Record<string, string>;
  active: boolean;
}

const ease = [0.2, 0.8, 0.2, 1] as const;
export const WISHLIST_DOMAINS: Record<string, string> = {
  amazon: 'amazon.com', throne: 'throne.com', wishtender: 'wishtender.com', buymeacoffee: 'buymeacoffee.com', kofi: 'ko-fi.com',
};
const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.07, delayChildren: 0.08 } },
  exit: { transition: { staggerChildren: 0.03, staggerDirection: -1 } },
};
const fadeSlideUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease } },
  exit: { opacity: 0, y: -10, transition: { duration: 0.22 } },
};
const linkRow = {
  hidden: { opacity: 0, x: -16 },
  visible: (i: number) => ({
    opacity: 1, x: 0,
    transition: { delay: 0.18 + i * 0.08, duration: 0.55, ease },
  }),
  exit: { opacity: 0, x: -8, transition: { duration: 0.18 } },
};
const wishlistReveal = {
  hidden: { opacity: 0, y: 16, scale: 0.97 },
  visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.6, ease, delay: 0.5 } },
  exit: { opacity: 0, scale: 0.97, transition: { duration: 0.2 } },
};

export const MyLinksSection: React.FC<MyLinksSectionProps> = ({ t, active }) => {
  const { config } = useAppConfig();
  const streamKeyRef = useRef(0);
  if (active) streamKeyRef.current++;

  const linkTypes = SOCIAL_PLATFORMS.map((p) => ({ ...p, accent: !!p.accent, desc: t[`mylinks_${p.id}_desc`] || p.desc }));

  const handleOpenLink = (url: string) => {
    if (window.Telegram?.WebApp) {
      window.Telegram.WebApp.openLink(url);
    } else {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  };

  const activeLinks = linkTypes
    .map(type => {
      // Única fuente: la config (VITE_* generadas desde config.master.json, con override de admin en CloudStorage).
      const fromConfig = config.socialLinks?.find(l => l.id === type.id);
      const url = (fromConfig?.url && fromConfig.url.startsWith('http')) ? fromConfig.url : '';
      const enabled = url.startsWith('http'); // habilitado si tiene URL real
      return { ...type, url, enabled };
    })
    .filter(l => l.enabled);

  // Wishlist: viene de la config (admin override o VITE_WISHLIST_*).
  const wishlistUrl = (config.wishlist?.enabled && config.wishlist?.url?.startsWith('http'))
    ? config.wishlist.url
    : '';

  const wishlistProvider = config.wishlist?.provider || 'amazon';
  const wishlistDomain = WISHLIST_DOMAINS[wishlistProvider] || 'lista de deseos';

  return (
    <div className="absolute inset-0" style={{ color: 'var(--c-text)', fontFamily: 'var(--ff-body)' }}>
      <AnimatePresence mode="wait">
        {active && (
          <motion.div
            key="links-content"
            variants={stagger}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="absolute inset-0"
          >
            {/* Title area */}
            <motion.div
              variants={fadeSlideUp}
              style={{ position: 'absolute', top: 'var(--safe-top, 62px)', left: 18, right: 18, paddingTop: 'var(--sp-4)' }}
            >
              <div style={{
                fontFamily: 'var(--ff-body)', fontSize: 'var(--fs-micro)',
                letterSpacing: '0.22em', textTransform: 'uppercase',
                color: 'var(--c-crimson)', fontWeight: 600,
              }}>
                <StreamText key={`lk-eb-${streamKeyRef.current}`} text={`${String(activeLinks.length).padStart(2, '0')} ${t.mylinks_links_label || 'enlaces'}`} speed={35} delay={100} caret={false} />
              </div>
              <h2 style={{
                fontFamily: 'var(--ff-display)', fontSize: 'var(--fs-title)',
                margin: '8px 0 0 0', lineHeight: 0.88, textTransform: 'uppercase',
              }}>
                {t.mylinks_title_1 || "Mis"}<br/>
                <span style={{
                  fontFamily: 'var(--ff-italic)', fontStyle: 'italic',
                  fontSize: 'clamp(38px, 6vw, 50px)', color: 'var(--c-crimson)',
                  letterSpacing: '-0.01em', textTransform: 'none',
                }}>{t.mylinks_title_2 || "redes."}</span>
              </h2>
            </motion.div>

            {/* Links list (scrolls when the creator has many networks) */}
            <div style={{
              position: 'absolute', top: 'calc(var(--safe-top, 62px) + 150px)',
              left: 18, right: 18, bottom: wishlistUrl ? 236 : 130,
              overflowY: 'auto', WebkitOverflowScrolling: 'touch',
            }}>
              {activeLinks.map((l, i) => (
                <motion.div
                  key={l.tag}
                  variants={linkRow}
                  custom={i}
                  className="hover-lift"
                  whileTap={{ scale: 0.98 }}
                  onClick={() => handleOpenLink(l.url)}
                  role="link"
                  tabIndex={0}
                  aria-label={`Abrir ${l.name}`}
                  style={{
                    display: 'grid', gridTemplateColumns: '44px 1fr auto',
                    alignItems: 'center', gap: 14, padding: '14px 4px',
                    borderTop: i === 0 ? '1px solid var(--c-line)' : 'none',
                    borderBottom: '1px solid var(--c-line)',
                    cursor: 'pointer',
                  }}
                >
                  <motion.div
                    whileHover={{ scale: 1.08 }}
                    style={{
                      width: 44, height: 44,
                      border: `1px solid ${l.accent ? 'var(--c-crimson)' : 'var(--c-line)'}`,
                      background: l.accent ? 'rgb(var(--c-crimson-rgb) / 0.10)' : 'transparent',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontFamily: 'var(--ff-display)', fontSize: 18,
                      color: l.accent ? 'var(--c-crimson)' : 'var(--c-text)', letterSpacing: '0.04em',
                    }}>{l.tag}</motion.div>

                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 600 }}>{l.name}</div>
                    <div style={{ fontSize: 'var(--fs-caption)', color: 'var(--c-muted)', marginTop: 2, letterSpacing: '0.04em' }}>{l.desc}</div>
                  </div>

                  <div style={{
                    fontSize: 'var(--fs-caption)', color: 'var(--c-text-dim)', letterSpacing: '0.04em',
                    display: 'flex', alignItems: 'center', gap: 8,
                  }}>
                    <span>{socialHandle(l.id, l.url) || config.profile.telegramUsername}</span>
                    <span style={{
                      width: 22, height: 22, border: '1px solid var(--c-line)',
                      borderRadius: '50%', display: 'inline-flex',
                      alignItems: 'center', justifyContent: 'center',
                      color: 'var(--c-text-dim)',
                    }}>
                      <ArrowUpRight style={{ width: 12, height: 12 }} />
                    </span>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Wishlist */}
            {wishlistUrl && (
              <motion.div
                variants={wishlistReveal}
                className="hover-lift"
                whileTap={{ scale: 0.98 }}
                onClick={() => window.dispatchEvent(new CustomEvent('telegram-command', { detail: { command: 'wishlist' } }))}
                role="link"
                tabIndex={0}
                aria-label="Wishlist"
                style={{
                  position: 'absolute', bottom: 132, left: 18, right: 18,
                  border: '1px solid rgba(201,162,74,0.35)', padding: '16px 18px',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  background: 'linear-gradient(135deg, rgba(201,162,74,0.07) 0%, var(--c-surface) 60%)',
                  backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
                  cursor: 'pointer',
                }}
              >
                <div>
                  <div style={{
                    fontFamily: 'var(--ff-body)', fontSize: 'var(--fs-micro)',
                    letterSpacing: '0.22em', textTransform: 'uppercase',
                    color: 'var(--c-gold)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6,
                  }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2, color: 'var(--c-gold)' }}>
                      <Star style={{ width: 12, height: 12, fill: 'currentColor' }} />
                      <Star style={{ width: 12, height: 12, fill: 'currentColor' }} />
                      <Star style={{ width: 12, height: 12, fill: 'currentColor' }} />
                    </span>
                    Wishlist
                  </div>
                  <div style={{ fontFamily: 'var(--ff-italic)', fontStyle: 'italic', fontSize: 20, marginTop: 4 }}>
                    {t.mylinks_wishlist_subtitle || "¿quieres mimarme?"}
                  </div>
                  <div style={{ fontSize: 'var(--fs-micro)', color: 'var(--c-muted)', marginTop: 4, letterSpacing: '0.08em' }}>
                    {wishlistDomain} · lista de deseos
                  </div>
                </div>
                <button style={{
                  background: 'rgba(201,162,74,0.12)', color: 'var(--c-gold)',
                  border: '1px solid rgba(201,162,74,0.5)', padding: '10px 14px',
                  fontSize: 'var(--fs-micro)', letterSpacing: '0.18em', textTransform: 'uppercase',
                  fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4,
                  fontFamily: 'var(--ff-body)',
                }}>
                  {t.mylinks_wishlist_button || "Ver"} <Star style={{ width: 12, height: 12, fill: 'currentColor' }} />
                </button>
              </motion.div>
            )}

            {/* Bottom Meta */}
            <motion.div variants={fadeSlideUp} style={{ position: 'absolute', bottom: 90, left: 18 }}>
              <div style={{
                display: 'flex', alignItems: 'baseline', gap: 10,
                fontFamily: 'var(--ff-body)', color: 'var(--c-text-dim)',
                fontSize: 'var(--fs-caption)', letterSpacing: '0.18em', textTransform: 'uppercase',
              }}>
                <span style={{ fontFamily: 'var(--ff-display)', fontSize: 22, color: 'var(--c-text)', letterSpacing: '0.02em', lineHeight: 1 }}>04</span>
                <span style={{ color: 'var(--c-muted)' }}>/ 05</span>
                <span style={{ width: 24, height: 1, background: 'var(--c-line)', margin: '0 6px', display: 'inline-block' }}></span>
                <span>{t.mylinks_title || "Mis redes"}</span>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MyLinksSection;
